import { Button } from '@src/components/ui/button/button';
import React, { useState } from 'react';
import { RescheduleForm } from './reschedule-form';
import { ConfirmationDialog } from '../dialogs/confirmation-dialog';
import { formatDateTime } from '@src/utils/date/formatDateTime';
import moment from 'moment';
import PropTypes from 'prop-types';

export const RescheduleButton = ({ detail, onUpdateBookingSlot, ...props }) => {
    const [schedule, setSchedule] = useState({});
    const [openForm, setOpenForm] = useState(false);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleClick = () => {
        setOpenForm(true);
    };

    const handleSubmit = values => {
        setSchedule(values);
        setOpenConfirm(true);
        setOpenForm(false);
    };

    const handleReschedule = async () => {
        const datePart = schedule.date.format('YYYY-MM-DD');
        const startPart = schedule.startTime.format('HH:mm:ss');
        const endPart = schedule.endTime?.format('HH:mm:ss');
        const duration = detail.listing.bookingSlot.duration;
        const durationUnit = detail.listing.bookingSlot.durationUnit;
        const timeZoneID = detail.listing.bookingSlot.timeZoneID;
        const timeZoneOffset = detail.listing.bookingSlot.timeZoneOffset;

        const fromDate = moment(`${datePart} ${startPart}`, 'YYYY-MM-DD HH:mm:ss', true);
        const toDate = endPart ? moment(`${datePart} ${endPart}`, 'YYYY-MM-DD HH:mm:ss', true) : null;

        setIsLoading(true);
        await new Promise(resolve => {
            onUpdateBookingSlot(
                {
                    OrderID: detail.orderId,
                    ID: detail.listing.cartItemId,
                    ItemDetailID: detail.listing.itemDetailId,
                    // MKTPLC-353: a booking is always at least 1 unit. listing.quantity
                    // comes from the Type-2 offer detail, which is 0 for service orders,
                    // and the backend rejects Quantity <= 0 (422), blocking the reschedule.
                    Quantity: detail.listing.quantity || 1,
                    Notes: detail.listing.notes,
                    BookingSlot: {
                        Duration: duration,
                        DurationUnit: durationUnit,
                        FromDateTime: fromDate.toDate().getTime() / 1000,
                        ToDateTime: endPart ? toDate.toDate().getTime() / 1000 : 0,
                        TimeZoneID: timeZoneID,
                        TimeZoneOffset: timeZoneOffset
                    }
                },
                result => resolve(result)
            );
        });

        setIsLoading(false);
        setOpenConfirm(false);
    };

    return (
        <>
            <Button {...props} onClick={handleClick}>
                Reschedule
            </Button>
            <RescheduleForm
                open={openForm}
                onClose={() => setOpenForm(false)}
                onSubmit={handleSubmit}
                detail={detail}
            />
            <ConfirmationDialog
                open={openConfirm}
                onClose={() => setOpenConfirm(false)}
                onAgree={handleReschedule}
                agreeLabel='OK'
                loading={isLoading}
                content={{
                    body: (
                        <span className='tw-font-[500]'>
                            Please confirm the new date and time with the customer before making changes
                        </span>
                    ),
                    description: (
                        <>
                            <div className='tw-flex tw-w-full tw-gap-[16px]'>
                                <span className='tw-w-[100px] tw-text-[16px] tw-leading-[24px] tw-font-[500]'>
                                    Task Date:
                                </span>
                                <span className='tw-text-[16px] tw-leading-[24px]'>
                                    {formatDateTime(schedule.date, 'MM/DD/YYYY')}
                                </span>
                            </div>
                            <div className='tw-flex tw-w-full tw-gap-[16px]'>
                                <span className='tw-w-[100px] tw-text-[16px] tw-leading-[24px] tw-font-[500]'>
                                    Time:
                                </span>
                                <span className='tw-text-[16px] tw-leading-[24px]'>
                                    {[schedule.startTime, schedule.endTime]
                                        .filter(Boolean)
                                        .map(time => formatDateTime(time, 'h:mm A'))
                                        .join(' to ')}
                                </span>
                            </div>
                        </>
                    )
                }}
            />
        </>
    );
};

RescheduleButton.propTypes = {
    detail: PropTypes.shape({
        orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        listing: PropTypes.shape({
            cartItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            itemDetailId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            quantity: PropTypes.number.isRequired,
            notes: PropTypes.string,
            bookingSlot: PropTypes.shape({
                duration: PropTypes.number.isRequired,
                durationUnit: PropTypes.string,
                timeZoneID: PropTypes.string,
                timeZoneOffset: PropTypes.number
            }).isRequired
        }).isRequired
    }).isRequired,
    onUpdateBookingSlot: PropTypes.func.isRequired
};
